import React, { useState } from "react";
import { Head, useForm, router } from "@inertiajs/react";

interface Domain {
  id: number;
  name: string;
  enabled: boolean;
  email_records: any[];
}

interface Props {
  domains: Domain[];
  errors?: any;
}

export default function Index({ domains, errors }: Props) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [domainFilter, setDomainFilter] = useState("");

  const { data, setData, post, processing, reset } = useForm({
    name: "",
    enabled: true,
  });

  // Filter domains based on search
  const filteredDomains = domainFilter
    ? domains.filter((domain) =>
        domain.name.toLowerCase().includes(domainFilter.toLowerCase())
      )
    : domains;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post("/domains", {
      onSuccess: () => {
        reset();
        setShowAddForm(false);
      },
    });
  };

  const toggleDomain = (domain: Domain) => {
    router.patch(`/domains/${domain.id}`, {
      enabled: !domain.enabled,
    });
  };

  const deleteDomain = (domain: Domain) => {
    if (
      confirm(`Are you sure you want to delete the domain "${domain.name}"?`)
    ) {
      router.delete(`/domains/${domain.id}`);
    }
  };

  return (
    <>
      <Head title="Manage Domains" />

      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Manage Domains
            </h1>
            <p className="text-gray-600">
              Add domains to track email deliverability
            </p>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="flex flex-wrap gap-3 items-center flex-1">
                <div className="flex-1 max-w-sm">
                  <input
                    type="text"
                    value={domainFilter}
                    onChange={(e) => setDomainFilter(e.target.value)}
                    placeholder="Search domains..."
                    className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  />
                </div>
                {domainFilter && (
                  <button
                    onClick={() => setDomainFilter("")}
                    className="inline-flex items-center gap-2 px-3 h-9 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors cursor-pointer"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Clear search
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-b from-blue-500 to-blue-600 text-white text-sm font-medium rounded-md transition-colors cursor-pointer"
              >
                {showAddForm ? "Cancel" : "Add Domain"}
              </button>
            </div>
          </div>

          {/* Add Domain Form */}
          {showAddForm && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Domain Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={data.name}
                    onChange={(e) => setData("name", e.target.value)}
                    placeholder="example.com"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                  {errors?.name && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.name[0]}
                    </p>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enabled"
                    checked={data.enabled}
                    onChange={(e) => setData("enabled", e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="enabled"
                    className="ml-2 block text-sm text-gray-700"
                  >
                    Enable tracking for this domain
                  </label>
                </div>

                <div className="flex space-x-4">
                  <button
                    type="submit"
                    disabled={processing}
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-b from-blue-500 to-blue-600 disabled:opacity-50 text-white text-sm font-medium rounded-md transition-colors cursor-pointer"
                  >
                    {processing ? "Adding..." : "Add Domain"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      reset();
                    }}
                    className="inline-flex items-center px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md border border-gray-300 transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Main Content */}
          <div className="space-y-6">
            {domains.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                    <svg
                      className="h-6 w-6 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No domains added yet
                  </h3>
                  <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                    Add a domain to start tracking where emails from that sender
                    land in your seed emails.
                  </p>
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <svg
                      className="h-4 w-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add Your First Domain
                  </button>
                </div>
              </div>
            ) : filteredDomains.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                    <svg
                      className="h-6 w-6 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No domains found
                  </h3>
                  <p className="text-gray-500">
                    No domains match your search criteria.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Domain
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Emails Tracked
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredDomains.map((domain) => (
                        <tr
                          key={domain.id}
                          className="hover:bg-gray-50/50 transition-colors duration-150"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {domain.name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-md ${
                                domain.enabled
                                  ? "bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20"
                                  : "bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10"
                              }`}
                            >
                              {domain.enabled ? "Enabled" : "Disabled"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <div className="flex items-center">
                              <span className="font-medium">
                                {domain.email_records?.length || 0}
                              </span>
                              <span className="ml-1 text-gray-500">emails</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end space-x-3">
                              <button
                                onClick={() => toggleDomain(domain)}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all cursor-pointer ${
                                  domain.enabled
                                    ? "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                                    : "text-green-700 hover:text-green-900 hover:bg-green-50"
                                }`}
                              >
                                {domain.enabled ? "Disable" : "Enable"}
                              </button>
                              <button
                                onClick={() => deleteDomain(domain)}
                                className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-all cursor-pointer"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
